import Link from 'next/link';
import React from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { HubTile } from '@web/features/hub-rework/HubTile';
import { Button } from '@web/ui/components';
import { SharpImage } from '@web/ui/components/SharpImage';
import { PersonaLogoCompact } from '@web/ui/icons';
import { useDesignsTokenTheme } from '@web/ui/theme/ThemeProvider';
import { env } from '@web/utils/env';

import DarkBackgroundImage from './dark-background.png';
import LightBackgroundImage from './light-background.png';

const SHubTile = styled(HubTile)`
  padding: 0;
`;

const StyledSharpImage = styled(SharpImage)`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`;

const TextContent = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: ${({ theme }) => theme.spacing('0750')};
`;

const Title = styled.span`
  white-space: pre;
  color: ${({ theme }) => theme.color.onBlue};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['10']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.decreased};
`;

const Subtitle = styled.span`
  color: ${({ theme }) => theme.color.onBlue};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-top: ${({ theme }) => theme.spacing('2000')};
  padding-right: ${({ theme }) => theme.spacing('2000')};
  padding-left: ${({ theme }) => theme.spacing('2000')};
  gap: ${({ theme }) => theme.spacing('1500')};
`;

const ButtonContainer = styled.div`
  position: relative;
  z-index: 1;
  padding: ${({ theme }) => theme.spacing('2000')};
  padding-top: 0;
`;

const SButton = styled(Button)`
  background-color: ${({ theme }) => theme.color.green};
  width: 100%;
`;

export const TimeFallValleyTile = () => {
  const t = useTranslations();
  const { isDarkMode } = useDesignsTokenTheme();

  // In the public build this is an illustrative tile and nothing more: no
  // link, and no call to action, because the Valley experience behind it is
  // still work in progress and there is nowhere to send anyone.
  if (env.SHELL_MODE) {
    return (
      <SHubTile textColor="onBlue" background="blue" stateLayer="onBlue">
        <TopContainer>
          <PersonaLogoCompact size="4000" color="onBackground" />
          <TextContent>
            <Title>{t('TIME_FALL_VALLEY_HUB_TILE_SUBTITLE')}</Title>
            <Subtitle>{t('USER_HUB_TILE_NOT_CONNECTED_SUBTITLE')}</Subtitle>
          </TextContent>
        </TopContainer>

        <StyledSharpImage
          src={isDarkMode ? DarkBackgroundImage : LightBackgroundImage}
        />
      </SHubTile>
    );
  }

  return (
    <Link href="/valley">
      <SHubTile
        textColor="onBlue"
        background="blue"
        stateLayer="onBlue"
      >
        <TopContainer>
          <PersonaLogoCompact size="4000" color="onBackground" />
          <TextContent>
            <Title>{t('TIME_FALL_VALLEY_HUB_TILE_SUBTITLE')}</Title>
            <Subtitle>{t('USER_HUB_TILE_NOT_CONNECTED_SUBTITLE')}</Subtitle>
          </TextContent>
        </TopContainer>

        <StyledSharpImage
          src={isDarkMode ? DarkBackgroundImage : LightBackgroundImage}
        />

        <ButtonContainer>
          <SButton label={t('TIME_FALL_VALLEY_HUB_TILE_ENTER_BUTTON')} />
        </ButtonContainer>
      </SHubTile>
    </Link>
  );
};
